import React, { type ComponentType, useMemo, useState } from 'react';
import { useAppointmentTypes } from '@features/appointment-types';
import {
  CustomRecurrenceModal,
  type CustomRecurrenceRule,
  getRecurrenceText,
  RECURRENCE_OPTIONS,
} from '@features/appointments';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useForms } from '@features/forms';
import { type Clinician, useClinicians } from '@features/organizations';
import { useDebounce } from '@hooks/useDebounce';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import RepeatOutlined from '@mui/icons-material/RepeatOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import { Box, InputBase, type SvgIconProps, Typography } from '@mui/material';
import { formatAppointmentDuration } from '@utils/durationUtils';
import { format } from 'date-fns';
import {
  type Options as RRuleOptions,
  RRule,
  type Weekday,
  type WeekdayStr,
} from 'rrule';

import {
  type AttachedItem,
  useAppointmentOverrides,
  useMergedAppointment,
} from '../../context/AppointmentOverridesContext';
import {
  type ExperimentalAppointment,
  getFixtureProvider,
  getFixtureProviders,
  type Provider,
} from '../../fixtures';
import { getExtendedColor, providerInitials } from '../../theme/extendedPalette';
import {
  getVisitStatusDisplay,
  VISIT_STATUSES,
  type VisitStatus,
} from '../../theme/visitStatus';

import { EditablePropertyRow } from './EditablePropertyRow';

export type PropertyRowKey =
  | 'status'
  | 'visit_status'
  | 'provider'
  | 'type'
  | 'telehealth'
  | 'time'
  | 'duration'
  | 'recurrence'
  | 'forms'
  | 'reminders'
  | 'notes';

interface Props {
  appointment: ExperimentalAppointment;
  /** When provided, only render these rows (drawer = full, popover = subset). */
  rows?: PropertyRowKey[];
}

// Linear-style dense properties table.
// Phase 1: read-only rows.
// Phase 2: Notes + Telehealth are click-to-edit via inline Floating UI popovers.
// Phase 3+: Status, Provider, Type, Date/Duration pickers.
export const AppointmentPropertiesTable = ({ appointment, rows }: Props) => {
  const showRow = (key: PropertyRowKey) => !rows || rows.includes(key);
  // Read-through override-aware appointment so the table reflects edits made
  // here, in the hover popover, and on the calendar block consistently.
  const merged = useMergedAppointment(appointment);
  const { setOverride, getOverride } = useAppointmentOverrides();
  const ovr = getOverride(appointment.appointment_id);

  // Provider display reads from fixtures first (preserves the colored demo
  // badges for seed appointments) then falls back to real clinicians for
  // anyone the user picked from the org list. RTK dedupes the query, so
  // calling useClinicians here and inside ProviderEditor is safe.
  const { clinicians: realClinicians } = useClinicians();
  const provider = lookupProvider(merged.provider_id, realClinicians);
  const reality = merged.block_state?.reality;
  const qualifiers = merged.block_state?.qualifiers;
  const start = new Date(merged.start_time);

  const noteValue = merged.notes ?? '';
  const telehealth = qualifiers?.telehealth ?? false;
  const setNoteValue = (v: string) => setOverride(merged.appointment_id, { notes: v });
  const setTelehealth = (v: boolean) => setOverride(merged.appointment_id, { telehealth: v });
  const setProvider = (id: string | null) =>
    setOverride(merged.appointment_id, { provider_id: id });
  const setType = (visit_type: string, cpt: string, duration?: number | null) => {
    setOverride(merged.appointment_id, {
      visit_type,
      cpt,
      // Picking a type bumps the duration too when the type carries one —
      // matches production behavior and is a satisfying side effect.
      ...(duration != null ? { duration } : {}),
    });
  };
  const setDuration = (d: number) => setOverride(merged.appointment_id, { duration: d });
  const setStartTime = (iso: string) => setOverride(merged.appointment_id, { start_time: iso });
  const setVisitStatus = (s: VisitStatus) =>
    setOverride(merged.appointment_id, { visit_status: s });
  const setForms = (forms: AttachedItem[]) =>
    setOverride(merged.appointment_id, { forms });
  const setReminders = (reminders: AttachedItem[]) =>
    setOverride(merged.appointment_id, { reminders });
  const setRecurrence = (recurrence: string | null) =>
    setOverride(merged.appointment_id, { recurrence });

  const status = getStatusDisplay(reality, qualifiers?.confirmed ?? false);
  const typeLabel = [merged.visit_type, merged.cpt].filter(Boolean).join(' · ');
  const visitStatus = getVisitStatusDisplay(merged.visit_status);

  // Custom-recurrence modal state lives at the table level so the modal
  // survives the EditablePropertyRow popover unmount when the user clicks
  // "Custom…" — see RecurrenceEditor's onOpenCustom plumbing.
  const [customRecurrenceOpen, setCustomRecurrenceOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {showRow('status') && (
        <PropertyRow icon={CheckCircleOutlined} label="Status">
          <StatusValue color={status.color} label={status.label} />
        </PropertyRow>
      )}

      {showRow('visit_status') && (
        <EditablePropertyRow
          icon={visitStatus.icon}
          label="Visit status"
          popoverWidth={220}
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: visitStatus.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
                {visitStatus.label}
              </Typography>
            </Box>
          }
          editor={({ onClose }) => (
            <VisitStatusEditor
              value={merged.visit_status ?? 'not_started'}
              onChange={(v) => {
                setVisitStatus(v);
                onClose();
              }}
            />
          )}
        />
      )}

      {showRow('provider') && (
        <EditablePropertyRow
          icon={PersonOutlined}
          label="Provider"
          popoverWidth={260}
          value={
            provider ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ProviderBadge provider={provider} />
                <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
                  {provider.title ? `${provider.name}, ${provider.title}` : provider.name}
                </Typography>
              </Box>
            ) : (
              <EmptyValue>Unassigned</EmptyValue>
            )
          }
          editor={({ onClose }) => (
            <ProviderEditor
              value={merged.provider_id ?? null}
              onChange={(id) => {
                setProvider(id);
                onClose();
              }}
            />
          )}
        />
      )}

      {showRow('type') && (
        <EditablePropertyRow
          icon={LocalHospitalOutlined}
          label="Type"
          popoverWidth={260}
          value={
            typeLabel ? <PlainValue>{typeLabel}</PlainValue> : <EmptyValue>Set type…</EmptyValue>
          }
          editor={({ onClose }) => (
            <AppointmentTypeEditor
              visitType={merged.visit_type ?? null}
              cpt={merged.cpt ?? null}
              onChange={(v, c, d) => {
                setType(v, c, d);
                onClose();
              }}
            />
          )}
        />
      )}

      {showRow('telehealth') && (
        <EditablePropertyRow
          icon={VideocamOutlined}
          label="Telehealth"
          popoverWidth={220}
          value={<PlainValue>{telehealth ? 'Yes' : 'In-person'}</PlainValue>}
          editor={({ onClose }) => (
            <TelehealthEditor
              value={telehealth}
              onChange={(v) => {
                setTelehealth(v);
                onClose();
              }}
            />
          )}
        />
      )}

      {showRow('time') && (
        <EditablePropertyRow
          icon={EventOutlined}
          label="Time"
          popoverWidth={260}
          value={<PlainValue>{format(start, 'EEE MMM d · h:mm a')}</PlainValue>}
          editor={() => <TimeEditor value={merged.start_time} onChange={setStartTime} />}
        />
      )}

      {showRow('duration') && (
        <EditablePropertyRow
          icon={TimerOutlined}
          label="Duration"
          popoverWidth={240}
          value={
            merged.duration ? (
              <PlainValue>{merged.duration} min</PlainValue>
            ) : (
              <EmptyValue>Set duration…</EmptyValue>
            )
          }
          editor={({ onClose }) => (
            <DurationEditor
              value={merged.duration ?? null}
              startTime={merged.start_time}
              onChange={setDuration}
              onClose={onClose}
            />
          )}
        />
      )}

      {showRow('recurrence') && (() => {
        const recValue = ovr?.recurrence !== undefined ? ovr.recurrence : recurrenceText(merged);
        return (
          <EditablePropertyRow
            icon={RepeatOutlined}
            label="Recurrence"
            popoverWidth={220}
              value={
              recValue ? <PlainValue>{recValue}</PlainValue> : <EmptyValue>One-time</EmptyValue>
            }
            editor={({ onClose }) => (
              <RecurrenceEditor
                value={recValue}
                onChange={(v) => {
                  setRecurrence(v);
                  onClose();
                }}
                onOpenCustom={() => {
                  onClose();
                  setCustomRecurrenceOpen(true);
                }}
              />
            )}
          />
        );
      })()}

      {showRow('forms') && (() => {
        const formsValue: AttachedItem[] =
          ovr?.forms !== undefined ? ovr.forms : formItems(merged);
        return (
          <EditablePropertyRow
            icon={AssignmentOutlined}
            label="Forms"
            popoverWidth={300}
              value={
              <ChipList
                items={formsValue.map((f) => ({ name: f.title, timing: f.timing }))}
                emptyLabel="No forms"
              />
            }
            editor={() => <FormsEditor value={formsValue} onChange={setForms} />}
          />
        );
      })()}

      {showRow('reminders') && (() => {
        const remindersValue: AttachedItem[] =
          ovr?.reminders !== undefined ? ovr.reminders : reminderItems(merged);
        return (
          <EditablePropertyRow
            icon={NotificationsOutlined}
            label="Reminders"
            popoverWidth={260}
              value={
              <ChipList
                items={remindersValue.map((r) => ({ name: r.title, timing: r.timing }))}
                emptyLabel="No reminders"
              />
            }
            editor={() => (
              <RemindersEditor value={remindersValue} onChange={setReminders} />
            )}
          />
        );
      })()}

      {showRow('notes') && (
        <EditablePropertyRow
          icon={StickyNote2Outlined}
          label="Notes"
          popoverWidth={320}
          value={
            noteValue ? (
              <Typography
                variant="body2"
                // Notes wrap and scroll inline so the user can read the whole
                // note without opening the editor. The wheel-scroll target is
                // this Typography; clicks still bubble to the row handler and
                // open the editor.
                onWheel={(e) => e.stopPropagation()}
                sx={{
                  fontSize: 13,
                  color: ELIG_TOKENS.ink,
                  fontStyle: 'italic',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  minHeight: '2.8em',
                  maxHeight: '8.4em',
                  overflowY: 'auto',
                }}
              >
                {noteValue}
              </Typography>
            ) : (
              <EmptyValue>Add a note…</EmptyValue>
            )
          }
          editor={({ onClose }) => (
            <NoteEditor value={noteValue} onChange={setNoteValue} onClose={onClose} />
          )}
        />
      )}

      <CustomRecurrenceModal
        open={customRecurrenceOpen}
        onClose={() => setCustomRecurrenceOpen(false)}
        onSave={(rule) => {
          const rrule = buildCustomRRule(rule);
          setRecurrence(getRecurrenceText(rrule));
          setCustomRecurrenceOpen(false);
        }}
      />
    </Box>
  );
};

// ─── Inline editors ───────────────────────────────────────────────────────

const COMMON_DURATIONS = [15, 30, 45, 50, 60, 90];

const ProviderEditor = ({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) => {
  // Real org clinicians come straight from RTK. Fixture providers (synthetic
  // demo IDs like `prov_carter`) are kept in the list so the seed data stays
  // round-trippable — the user can still re-pick the demo provider after
  // overriding to a real one. IDs don't collide; if they ever did, fixtures
  // win to preserve their colored badges.
  const { clinicians, isLoading } = useClinicians();
  const fixtureProviders = getFixtureProviders();

  const merged: Provider[] = useMemo(() => {
    const fixtureIds = new Set(fixtureProviders.map((p) => p.provider_id));
    const realAsProviders: Provider[] = clinicians
      .filter((c) => !fixtureIds.has(c.userId))
      .map((c) => ({ provider_id: c.userId, name: c.name }));
    return [...fixtureProviders, ...realAsProviders];
  }, [clinicians, fixtureProviders]);

  return (
    <Box sx={{ py: 0.5, maxHeight: 280, overflowY: 'auto' }}>
      {merged.map((p) => (
        <OptionRow
          key={p.provider_id}
          selected={value === p.provider_id}
          onClick={() => onChange(p.provider_id)}
        >
          <ProviderBadge provider={p} />
          <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
            {p.title ? `${p.name}, ${p.title}` : p.name}
          </Typography>
        </OptionRow>
      ))}
      {isLoading && clinicians.length === 0 && (
        <EditorHint>Loading clinicians…</EditorHint>
      )}
      <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
      <OptionRow selected={value === null} onClick={() => onChange(null)}>
        <Box sx={{ width: 22 }} />
        <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.inkSoft }}>
          Unassigned
        </Typography>
      </OptionRow>
    </Box>
  );
};

const AppointmentTypeEditor = ({
  visitType,
  cpt,
  onChange,
}: {
  visitType: string | null;
  cpt: string | null;
  onChange: (visitType: string, cpt: string, duration?: number | null) => void;
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const { appointmentTypes, isLoading } = useAppointmentTypes(
    debouncedSearch || undefined,
  );

  return (
    <Box>
      <Box sx={{ p: 1, borderBottom: `1px solid ${ELIG_TOKENS.line}` }}>
        <InputBase
          autoFocus
          placeholder="Search types…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ fontSize: 13, color: ELIG_TOKENS.ink, px: 1, py: 0.5 }}
        />
      </Box>
      <Box sx={{ maxHeight: 240, overflowY: 'auto', py: 0.5 }}>
        {isLoading && appointmentTypes.length === 0 && (
          <EditorHint>Loading types…</EditorHint>
        )}
        {!isLoading && appointmentTypes.length === 0 && (
          <EditorHint>{search ? 'No types match' : 'No types available'}</EditorHint>
        )}
        {appointmentTypes.map((t) => {
          const typeCpt = t.default_cpt_codes?.[0] ?? '';
          const isSelected = visitType === t.encounter_type && cpt === typeCpt;
          return (
            <OptionRow
              key={t.encounter_type_id}
              selected={isSelected}
              onClick={() =>
                onChange(t.encounter_type, typeCpt, t.default_duration ?? null)
              }
            >
              <Typography
                variant="body2"
                sx={{ flex: 1, fontSize: 13, color: ELIG_TOKENS.ink }}
              >
                {t.encounter_type}
              </Typography>
              {typeCpt && (
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}
                >
                  {typeCpt}
                </Typography>
              )}
            </OptionRow>
          );
        })}
      </Box>
    </Box>
  );
};

const DurationEditor = ({
  value,
  startTime,
  onChange,
  onClose,
}: {
  value: number | null;
  startTime: string;
  onChange: (d: number) => void;
  onClose: () => void;
}) => {
  const startMs = new Date(startTime).getTime();
  const isCustom = value != null && !COMMON_DURATIONS.includes(value);
  const [customDraft, setCustomDraft] = React.useState(isCustom ? String(value) : '');
  const [endDraft, setEndDraft] = React.useState(() =>
    formatTimeForInput(new Date(startMs + (value ?? 0) * 60_000)),
  );

  const commitCustom = () => {
    const n = parseInt(customDraft, 10);
    if (Number.isFinite(n) && n > 0) onChange(n);
  };

  const commitEnd = () => {
    const dur = durationFromEndTime(endDraft, startMs);
    if (dur != null && dur > 0) onChange(dur);
  };

  return (
    <Box sx={{ py: 0.5 }}>
      {COMMON_DURATIONS.map((d) => (
        <OptionRow
          key={d}
          selected={value === d}
          onClick={() => {
            onChange(d);
            onClose();
          }}
        >
          <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink, flex: 1 }}>
            {d} min
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
            until {formatTimeOfDay(new Date(startMs + d * 60_000))}
          </Typography>
        </OptionRow>
      ))}

      <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />

      <DurationInputRow
        label="Custom"
        suffix="min"
        type="number"
        placeholder="50"
        value={customDraft}
        selected={isCustom}
        onChange={setCustomDraft}
        onCommit={commitCustom}
        inputWidth={56}
      />

      <DurationInputRow
        label="End at"
        type="time"
        value={endDraft}
        selected={false}
        onChange={setEndDraft}
        onCommit={commitEnd}
        inputWidth={92}
      />
    </Box>
  );
};

const DurationInputRow = ({
  label,
  value,
  selected,
  type,
  suffix,
  placeholder,
  inputWidth,
  onChange,
  onCommit,
}: {
  label: string;
  value: string;
  selected: boolean;
  type: 'number' | 'time';
  suffix?: string;
  placeholder?: string;
  inputWidth: number;
  onChange: (v: string) => void;
  onCommit: () => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      bgcolor: selected ? 'rgba(28, 30, 133, 0.05)' : 'transparent',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontSize: 13,
        color: selected ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft,
        fontWeight: selected ? 600 : 400,
      }}
    >
      {label}
    </Typography>
    <InputBase
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: inputWidth,
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        '&.Mui-focused': { borderColor: ELIG_TOKENS.midnight },
        // Hide native number-input spin buttons
        '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
        '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }}
    />
    {suffix && (
      <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
        {suffix}
      </Typography>
    )}
  </Box>
);

const formatTimeOfDay = (d: Date): string => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
};

// 24-hour HH:MM for native <input type="time">
const formatTimeForInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const durationFromEndTime = (timeStr: string, startMs: number): number | null => {
  const [hStr, mStr] = timeStr.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const end = new Date(startMs);
  end.setHours(h, m, 0, 0);
  let durMin = Math.round((end.getTime() - startMs) / 60_000);
  // If the user enters an earlier time (e.g., 12:30 when the start is 14:00),
  // assume they meant the next occurrence rather than yesterday.
  if (durMin <= 0) durMin += 24 * 60;
  return durMin;
};

const TimeEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (iso: string) => void;
}) => (
  // Native datetime-local fires onChange only with complete valid values, so
  // we can autosave directly. Popover stays open until the user dismisses
  // (outside-click / Esc) — same as Custom / End-at in the Duration editor.
  <Box sx={{ p: 1.5 }}>
    <InputBase
      type="datetime-local"
      autoFocus
      value={toLocalDatetime(value)}
      onChange={(e) => {
        if (e.target.value) onChange(fromLocalDatetime(e.target.value));
      }}
      sx={{
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1,
        px: 1,
        py: 0.5,
        width: '100%',
        '&.Mui-focused': { borderColor: ELIG_TOKENS.midnight },
      }}
    />
  </Box>
);

// ─── Forms / Reminders / Recurrence editors ──────────────────────────────

// Source: SchedulingForm.tsx in @features/appointment-types — single source
// of truth for timing offsets.
interface TimingPreset {
  display: string;
  iso: string;
}
const FORM_TIMING_PRESETS: TimingPreset[] = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
  { display: '5 minutes after appointment', iso: 'PT5M' },
];
const REMINDER_TIMING_PRESETS: TimingPreset[] = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
];
const DEFAULT_FORM_OFFSET = '-P1D';
const DEFAULT_REMINDER_OFFSET = '-P1D';

// Common reminder templates — production has no dedicated reminder-templates
// endpoint, so this is the placeholder source.
const COMMON_REMINDERS: AttachedItem[] = [
  { id: 'sms', title: 'SMS' },
  { id: 'email', title: 'Email' },
];

// Map CustomRecurrenceModal output (CustomRecurrenceRule) to an rrule string
// without dragging in the full useRecurrence hook (which carries its own
// internal state and would be awkward to drive externally). Mirrors the
// subset of rrule options that the modal can produce.
const FREQ_MAP: Record<string, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
};
const DAY_MAP: Record<WeekdayStr, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};
const buildCustomRRule = (rule: CustomRecurrenceRule): string => {
  const options: Partial<RRuleOptions> = {
    freq: FREQ_MAP[rule.frequency] ?? RRule.WEEKLY,
    interval: rule.interval || 1,
  };
  if (rule.frequency === 'weekly' && rule.weekDays.length) {
    options.byweekday = rule.weekDays.map((d) => DAY_MAP[d as WeekdayStr]);
  }
  if (rule.endMode === 'on' && rule.endDate) {
    options.until = new Date(rule.endDate);
  } else if (rule.endMode === 'after' && rule.endCount) {
    options.count = rule.endCount;
  }
  return new RRule(options).toString();
};

const FormsEditor = ({
  value,
  onChange,
}: {
  value: AttachedItem[];
  onChange: (items: AttachedItem[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { forms, isLoading } = useForms({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const selectedIds = new Set(value.map((f) => f.id));

  const addItem = (item: AttachedItem) => {
    onChange([
      ...value,
      { ...item, offset: DEFAULT_FORM_OFFSET, timing: formatAppointmentDuration(DEFAULT_FORM_OFFSET) },
    ]);
  };
  const removeItem = (id: string) => onChange(value.filter((f) => f.id !== id));
  const updateOffset = (id: string, offset: string) =>
    onChange(
      value.map((f) =>
        f.id === id ? { ...f, offset, timing: formatAppointmentDuration(offset) } : f,
      ),
    );

  // Filter unselected entries from the search query
  const unselected = forms
    .map(formEntryToAttached)
    .filter((f): f is AttachedItem => !!f && !selectedIds.has(f.id));

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map((item) => (
            <SelectedAttachedRow
              key={item.id}
              item={item}
              presets={FORM_TIMING_PRESETS}
              onChangeOffset={(o) => updateOffset(item.id, o)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ p: 1, borderBottom: `1px solid ${ELIG_TOKENS.line}` }}>
        <InputBase
          autoFocus
          placeholder="Search forms…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ fontSize: 13, color: ELIG_TOKENS.ink, px: 1, py: 0.5 }}
        />
      </Box>
      <Box sx={{ maxHeight: 240, overflowY: 'auto', py: 0.5 }}>
        {isLoading && forms.length === 0 && (
          <EditorHint>Loading forms…</EditorHint>
        )}
        {!isLoading && unselected.length === 0 && (
          <EditorHint>
            {search ? 'No forms match' : 'No forms available'}
          </EditorHint>
        )}
        {unselected.map((item) => (
          <OptionRow key={item.id} selected={false} onClick={() => addItem(item)}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '3px',
                border: `1.5px solid ${ELIG_TOKENS.lineStrong}`,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink, flex: 1 }}>
              {item.title}
            </Typography>
          </OptionRow>
        ))}
      </Box>
    </Box>
  );
};

// `useForms` returns FormWithRevisionSummary[] — id lives on `form.id`, the
// display title lives on `current_revision.title`.
const formEntryToAttached = (entry: unknown): AttachedItem | null => {
  const e = entry as {
    form?: { id?: string | null };
    current_revision?: { title?: string | null } | null;
  };
  const id = e?.form?.id ?? null;
  const title = e?.current_revision?.title ?? null;
  if (!id || !title) return null;
  return { id, title };
};

const RemindersEditor = ({
  value,
  onChange,
}: {
  value: AttachedItem[];
  onChange: (items: AttachedItem[]) => void;
}) => {
  const selectedIds = new Set(value.map((r) => r.id));
  const addItem = (item: AttachedItem) =>
    onChange([
      ...value,
      {
        ...item,
        offset: DEFAULT_REMINDER_OFFSET,
        timing: formatAppointmentDuration(DEFAULT_REMINDER_OFFSET),
      },
    ]);
  const removeItem = (id: string) => onChange(value.filter((r) => r.id !== id));
  const updateOffset = (id: string, offset: string) =>
    onChange(
      value.map((r) =>
        r.id === id ? { ...r, offset, timing: formatAppointmentDuration(offset) } : r,
      ),
    );

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map((item) => (
            <SelectedAttachedRow
              key={item.id}
              item={item}
              presets={REMINDER_TIMING_PRESETS}
              onChangeOffset={(o) => updateOffset(item.id, o)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <Box sx={{ py: 0.5 }}>
        {COMMON_REMINDERS.filter((r) => !selectedIds.has(r.id)).map((item) => (
          <OptionRow key={item.id} selected={false} onClick={() => addItem(item)}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '3px',
                border: `1.5px solid ${ELIG_TOKENS.lineStrong}`,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink, flex: 1 }}>
              {item.title}
            </Typography>
          </OptionRow>
        ))}
      </Box>
    </Box>
  );
};

// ─── Selected-row + timing dropdown ──────────────────────────────────────

const SelectedAttachedRow = ({
  item,
  presets,
  onChangeOffset,
  onRemove,
}: {
  item: AttachedItem;
  presets: TimingPreset[];
  onChangeOffset: (iso: string) => void;
  onRemove: () => void;
}) => {
  const [timingOpen, setTimingOpen] = useState(false);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'rgba(28, 30, 133, 0.04)',
        }}
      >
        <Box
          onClick={onRemove}
          sx={{
            width: 14,
            height: 14,
            borderRadius: '3px',
            border: `1.5px solid ${ELIG_TOKENS.midnight}`,
            bgcolor: ELIG_TOKENS.midnight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '1px', bgcolor: '#fff' }} />
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            color: ELIG_TOKENS.ink,
            fontWeight: 500,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setTimingOpen((o) => !o);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            cursor: 'pointer',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            transition: 'background-color 0.12s',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkSoft }}>
            {item.timing ?? (item.offset ? formatAppointmentDuration(item.offset) : 'Set timing')}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 9, color: ELIG_TOKENS.inkFaint }}>
            ▾
          </Typography>
        </Box>
      </Box>
      {timingOpen && (
        <Box sx={{ pl: 4, py: 0.25, bgcolor: 'rgba(28, 30, 133, 0.02)' }}>
          {presets.map((preset) => (
            <OptionRow
              key={preset.iso}
              selected={item.offset === preset.iso}
              onClick={() => {
                onChangeOffset(preset.iso);
                setTimingOpen(false);
              }}
            >
              <Typography variant="body2" sx={{ fontSize: 12, color: ELIG_TOKENS.ink, flex: 1 }}>
                {preset.display}
              </Typography>
            </OptionRow>
          ))}
        </Box>
      )}
    </Box>
  );
};

const EditorHint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }}>
      {children}
    </Typography>
  </Box>
);

const RecurrenceEditor = ({
  value,
  onChange,
  onOpenCustom,
}: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  onOpenCustom: () => void;
}) => {
  // Re-use production's option list verbatim; the popover stores the human
  // label so display reads can stay simple. Custom rules are stored as the
  // rrule-derived text (e.g. "every 2 weeks on Tuesday").
  const presets = RECURRENCE_OPTIONS.filter((o) => o.value !== 'custom');
  const customOption = RECURRENCE_OPTIONS.find((o) => o.value === 'custom');
  const isPresetSelected = (label: string) => (value ?? null) === label;

  return (
    <Box sx={{ py: 0.5 }}>
      {presets.map((preset) => {
        // 'none' stores null in the override; everything else stores the label.
        const stored = preset.value === 'none' ? null : preset.label;
        const selected =
          preset.value === 'none' ? !value : isPresetSelected(preset.label);
        return (
          <OptionRow
            key={preset.value}
            selected={selected}
            onClick={() => onChange(stored)}
          >
            <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
              {preset.label}
            </Typography>
          </OptionRow>
        );
      })}
      {customOption && (
        <>
          <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
          <OptionRow selected={false} onClick={onOpenCustom}>
            <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.midnight }}>
              {customOption.label}
            </Typography>
          </OptionRow>
        </>
      )}
    </Box>
  );
};

const ProviderBadge = ({ provider }: { provider: Provider }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 18,
      minWidth: 22,
      px: 0.5,
      borderRadius: '4px',
      // Real clinicians (sourced from useClinicians) carry no fixture color,
      // so they get a neutral grey badge. Fixture providers keep their hue.
      bgcolor: provider.color ? getExtendedColor(provider.color) : ELIG_TOKENS.inkSoft,
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {providerInitials(provider.name)}
  </Box>
);

const toLocalDatetime = (iso: string): string => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalDatetime = (local: string): string => new Date(local).toISOString();

const NoteEditor = ({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
}) => (
  <Box sx={{ p: 1.5 }}>
    <InputBase
      autoFocus
      multiline
      minRows={3}
      fullWidth
      placeholder="Add a note…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        // Cmd/Ctrl+Enter commits; bare Enter inserts a newline.
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          onClose();
        }
      }}
      sx={{
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        lineHeight: 1.4,
        '& textarea': {
          fontStyle: value ? 'normal' : 'italic',
          color: value ? ELIG_TOKENS.ink : ELIG_TOKENS.inkFaint,
        },
      }}
    />
  </Box>
);

const TelehealthEditor = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) => (
  <Box sx={{ py: 0.5 }}>
    <OptionRow icon={PersonOutlined} selected={!value} onClick={() => onChange(false)}>
      <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
        In-person
      </Typography>
    </OptionRow>
    <OptionRow icon={VideocamOutlined} selected={value} onClick={() => onChange(true)}>
      <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
        Telehealth
      </Typography>
    </OptionRow>
  </Box>
);

const VisitStatusEditor = ({
  value,
  onChange,
}: {
  value: VisitStatus;
  onChange: (v: VisitStatus) => void;
}) => (
  <Box sx={{ py: 0.5 }}>
    {VISIT_STATUSES.map((status) => {
      const display = getVisitStatusDisplay(status);
      return (
        <OptionRow
          key={status}
          icon={display.icon}
          selected={value === status}
          onClick={() => onChange(status)}
        >
          <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
            {display.label}
          </Typography>
        </OptionRow>
      );
    })}
  </Box>
);

const OptionRow = ({
  icon: Icon,
  selected,
  onClick,
  children,
}: {
  icon?: ComponentType<SvgIconProps>;
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      cursor: 'pointer',
      bgcolor: selected ? 'rgba(28, 30, 133, 0.05)' : 'transparent',
      transition: 'background-color 0.12s',
      '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.04)' },
    }}
  >
    {Icon && (
      <Icon sx={{ fontSize: 16, color: selected ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft }} />
    )}
    {children}
  </Box>
);

// ─── Read-only row primitives ─────────────────────────────────────────────

const PropertyRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 0.5,
      borderRadius: 1,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ELIG_TOKENS.inkSoft }}>
      <Icon sx={{ fontSize: 14 }} />
      <Typography
        variant="body2"
        sx={{ fontSize: 12, fontWeight: 500, color: ELIG_TOKENS.inkSoft }}
      >
        {label}
      </Typography>
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const PlainValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.ink,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const EmptyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.inkFaint,
      fontStyle: 'italic',
    }}
  >
    {children}
  </Typography>
);

const StatusValue = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
      {label}
    </Typography>
  </Box>
);

// ─── Read-only chip list (Forms / Reminders) ──────────────────────────────

interface ChipItem {
  name: string;
  timing?: string;
}

const ChipList = ({ items, emptyLabel }: { items: ChipItem[]; emptyLabel: string }) => {
  if (!items.length) return <EmptyValue>{emptyLabel}</EmptyValue>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'rgba(28, 30, 133, 0.06)',
            border: `1px solid ${ELIG_TOKENS.line}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: 12, color: ELIG_TOKENS.ink, fontWeight: 500 }}
          >
            {item.name}
          </Typography>
          {item.timing && (
            <Typography variant="caption" sx={{ fontSize: 10, color: ELIG_TOKENS.inkFaint }}>
              {item.timing}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

// ─── Production-side reads with prototype fallback ────────────────────────
//
// The properties below are typed on `AppointmentResponse` (form_rules,
// reminder_rules, series_id) but the fixture dump doesn't populate them.
// Read the real fields first; if missing, derive plausible demo content from
// visit_type / confirmation status so the UI shows variety across rows. When
// the BE/fixture starts populating these fields, these fallbacks become dead
// code automatically.

const formItems = (appt: ExperimentalAppointment): AttachedItem[] => {
  const real = (
    appt as {
      form_rules?: Array<{ form_definition_id?: string | null; title?: string | null }> | null;
    }
  ).form_rules;
  if (real && real.length) {
    return real.map((r, i) => ({
      id: r.form_definition_id ?? `${i}`,
      title: r.title ?? 'Form',
    }));
  }
  if (appt.visit_type === 'Therapy intake') {
    return [
      { id: 'demo-intake', title: 'Intake questionnaire', timing: '24h before' },
      { id: 'demo-consent', title: 'Consent form', timing: 'At visit' },
    ];
  }
  if (appt.visit_type === 'Therapy') {
    return [{ id: 'demo-phq9', title: 'PHQ-9', timing: '24h before' }];
  }
  return [];
};

const reminderItems = (appt: ExperimentalAppointment): AttachedItem[] => {
  const real = (
    appt as {
      reminder_rules?: Array<{
        reminder_template_id?: string | null;
        title?: string | null;
      }> | null;
    }
  ).reminder_rules;
  if (real && real.length) {
    return real.map((r, i) => ({
      id: r.reminder_template_id ?? `${i}`,
      title: r.title ?? 'Reminder',
    }));
  }
  if (appt.block_state?.qualifiers?.confirmed) {
    return [
      { id: 'demo-sms-24h', title: 'SMS', timing: '24h before' },
      { id: 'demo-email-day-of', title: 'Email', timing: 'Morning of' },
    ];
  }
  return [];
};

const recurrenceText = (appt: ExperimentalAppointment): string | null => {
  const seriesId = (appt as { series_id?: string | null }).series_id;
  if (seriesId) {
    // Real series — would parse `series.rrule` via useGetAppointmentSeriesQuery
    // and useRecurrence's getRecurrenceText. Stub for now.
    return 'Recurring';
  }
  // Demo: ongoing therapy is typically weekly. Use the production label so
  // the editor recognizes the value as already-selected.
  if (appt.visit_type === 'Therapy') return 'Weekly';
  return null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────

const lookupProvider = (
  id: string | null | undefined,
  clinicians: Clinician[],
): Provider | null => {
  if (!id) return null;
  const fixture = getFixtureProvider(id);
  if (fixture) return fixture;
  const cl = clinicians.find((c) => c.userId === id);
  return cl ? { provider_id: cl.userId, name: cl.name } : null;
};

const getStatusDisplay = (
  reality: string | undefined,
  confirmed: boolean,
): { color: string; label: string } => {
  if (reality === 'cancelled') return { color: ELIG_TOKENS.red, label: 'Cancelled' };
  if (reality === 'rescheduling') return { color: '#CA8A04', label: 'Reschedule requested' };
  if (reality === 'suggested') return { color: ELIG_TOKENS.midnight, label: 'Suggested' };
  if (reality === 'tentative') return { color: ELIG_TOKENS.inkFaint, label: 'Tentative' };
  if (reality === 'block') return { color: ELIG_TOKENS.inkFaint, label: 'Time block' };
  if (confirmed) return { color: ELIG_TOKENS.green, label: 'Confirmed' };
  return { color: ELIG_TOKENS.inkFaint, label: 'Unconfirmed' };
};
